import { useCallback, useMemo } from 'react';

import { useSearchParams } from 'react-router-dom';

import { TableFilterConfig, TableFilterState } from '../types/types';

/**
 * Hook for managing table filter state via URL search params
 */
export function useTableFilters(filtersConfig: TableFilterConfig[]) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get current filter values from URL
  const activeFilters = useMemo<TableFilterState>(() => {
    const filters: TableFilterState = {};
    const filterNames = new Set(filtersConfig.map(c => c.name));

    for (const [key, value] of searchParams.entries()) {
      if (filterNames.has(key)) {
        const allValues = searchParams.getAll(key);
        filters[key] = allValues.length > 1 ? allValues : value;
      }
    }

    return filters;
  }, [searchParams, filtersConfig]);

  // Count of active filters (excluding empty values)
  const activeFiltersCount = useMemo(() => {
    return Object.values(activeFilters).filter(value => {
      if (Array.isArray(value)) return value.length > 0;
      return value && value.trim() !== '';
    }).length;
  }, [activeFilters]);

  // Check if any filters are applied
  const hasActiveFilters = activeFiltersCount > 0;

  // Set a single filter value
  const setFilter = useCallback(
    (name: string, value: string | string[]) => {
      const newParams = new URLSearchParams(searchParams.toString());
      newParams.delete(name);

      const isEmpty =
        (typeof value === 'string' && value.trim() === '') ||
        (Array.isArray(value) && value.length === 0);

      if (!isEmpty) {
        if (Array.isArray(value)) {
          value.forEach(v => newParams.append(name, v));
        } else {
          newParams.set(name, value);
        }
      }

      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  // Clear a single filter
  const clearFilter = useCallback(
    (name: string) => {
      const newParams = new URLSearchParams(searchParams.toString());
      newParams.delete(name);
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    const newParams = new URLSearchParams(searchParams.toString());
    const filterNames = filtersConfig.map(c => c.name);

    filterNames.forEach(name => newParams.delete(name));
    setSearchParams(newParams);
  }, [searchParams, setSearchParams, filtersConfig]);

  // Apply saved filter (replace all current filters)
  const applyFilters = useCallback(
    (filters: TableFilterState) => {
      const newParams = new URLSearchParams(searchParams.toString());

      // Clear existing filter params
      filtersConfig.forEach(config => newParams.delete(config.name));

      // Apply new filters
      Object.entries(filters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(v => newParams.append(key, v));
        } else if (value && value.trim() !== '') {
          newParams.set(key, value);
        }
      });

      setSearchParams(newParams);
    },
    [searchParams, setSearchParams, filtersConfig]
  );

  // Remove a specific value from a multi-value filter
  const removeFilterValue = useCallback(
    (name: string, valueToRemove?: string) => {
      const currentValue = activeFilters[name];

      if (!valueToRemove) {
        // Remove entire filter
        clearFilter(name);
        return;
      }

      if (Array.isArray(currentValue)) {
        const newValue = currentValue.filter(v => v !== valueToRemove);
        if (newValue.length === 0) {
          clearFilter(name);
        } else {
          setFilter(name, newValue);
        }
      } else {
        // Single value - clear the filter
        clearFilter(name);
      }
    },
    [activeFilters, clearFilter, setFilter]
  );

  // Get filter value helper
  const getFilterValue = useCallback(
    (name: string): string[] => {
      const value = activeFilters[name];
      if (!value) return [];
      return Array.isArray(value) ? value : [value];
    },
    [activeFilters]
  );

  return {
    activeFilters,
    activeFiltersCount,
    hasActiveFilters,
    setFilter,
    clearFilter,
    clearAllFilters,
    applyFilters,
    removeFilterValue,
    getFilterValue,
  };
}
